import { Button } from "@hortis/ui/button";
import { colors } from "@hortis/ui/colors";
import { SwitchHorizontal1, XClose } from "@hortis/ui/icons";
import { MarkerPin1 } from "@hortis/ui/icons/marker-pin-1";
import { MarkerPinPlus1 } from "@hortis/ui/icons/marker-pin-plus-1";
import { useCallback, useState } from "react";
import { useWatch } from "react-hook-form";
import { Layer, Source } from "react-map-gl";
import {
  circleRadiusStyle,
  circleStrokeWidthStyle,
} from "src/components/map/material-clusters/material-cluster-layers";
import { MapPreviewOverlay } from "src/components/map/overlays/map-preview-overlay";
import { FormTextField } from "src/components/text-field";
import { ControlledMap } from "src/features/records/material/components/material-title-card/controlled-map";
import type { PositionZodInput } from "../schema";
import { MapPositionDialog } from "./map-position-dialog";

interface MaterialMapPositionFieldProps {
  value: PositionZodInput | null;
  onChange: (value: PositionZodInput | null) => void;
  trigger: () => Promise<boolean>;
  name: string;
}

export const MaterialMapPositionField = ({
  value: _,
  onChange,
  trigger,
  name,
}: MaterialMapPositionFieldProps) => {
  const [showFields, setShowFields] = useState(false);
  const value = useWatch<Record<typeof name, PositionZodInput | null>>({
    name,
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const toggleFields = useCallback(async () => {
    if (value?.latitude == null && value?.longitude == null) {
      setShowFields((x) => !x);
      return;
    }

    const valid = await trigger();
    if (valid) {
      setShowFields((x) => !x);
    }
  }, [value, trigger]);

  const onCoordinatePaste = useCallback(async () => {
    if (!("clipboard" in navigator)) {
      return;
    }
    const currentLat = value?.latitude;
    const currentLong = value?.longitude;
    const clipboardText = await navigator.clipboard.readText();
    const [clipboardLat, clipboardLong] = clipboardText
      .replace(" ", "")
      .split(",");
    if (
      clipboardLat == null ||
      clipboardLong == null ||
      Number.isNaN(Number(clipboardLat)) ||
      Number.isNaN(Number(clipboardLong)) ||
      currentLat != null ||
      currentLong != null
    ) {
      return;
    } else {
      onChange({
        latitude: clipboardLat,
        longitude: clipboardLong,
        elevation: null,
      });
    }
  }, [onChange, value]);

  const numericValue =
    value?.latitude == null || value.longitude == null
      ? null
      : {
          latitude: Number(value.latitude),
          longitude: Number(value.longitude),
        };
  const positioned = value != null && numericValue != null;

  return (
    <>
      {showFields ? (
        <div className="flex flex-col gap-8 rounded-lg border border-grey-200 bg-grey-50 px-4 py-6 shadow-xs md:px-8 md:py-8">
          <FormTextField
            label="Latitude"
            name={`${name}.latitude`}
            testId="latitude"
            type="text"
            signedNumeric
            decimal
            onPaste={onCoordinatePaste}
          />
          <FormTextField
            label="Longitude"
            name={`${name}.longitude`}
            testId="longitude"
            type="text"
            signedNumeric
            decimal
          />
          <FormTextField
            label="Elevation (m)"
            name={`${name}.elevation`}
            testId="elevation"
            type="text"
            signedNumeric
            decimal
          />
        </div>
      ) : (
        <div className="relative h-52">
          <ControlledMap
            useSiteCoordinates={value == null}
            latitude={numericValue?.latitude ?? 0}
            longitude={numericValue?.longitude ?? 0}
            staticMap
          >
            {positioned && (
              <Source
                id="map-geometry-2"
                type="geojson"
                data={{
                  type: "Feature",
                  properties: {},
                  geometry: {
                    type: "Point",
                    coordinates: [
                      numericValue.longitude,
                      numericValue.latitude,
                    ],
                  },
                }}
              >
                <Layer
                  id="unclustered-point-orange"
                  type="circle"
                  source="map-geometry-2"
                  filter={["!", ["has", "point_count"]]}
                  paint={{
                    "circle-color": colors.yellow[400],
                    "circle-radius": circleRadiusStyle,
                    "circle-stroke-width": circleStrokeWidthStyle,
                    "circle-stroke-color": colors.white,
                  }}
                />
              </Source>
            )}
          </ControlledMap>
          <MapPreviewOverlay blur={!positioned}>
            <Button
              size="xs"
              startIcon={positioned ? MarkerPin1 : MarkerPinPlus1}
              className="border-none shadow-sm"
              testId={
                positioned ? "edit-position-button" : "add-position-button"
              }
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              {positioned ? "Reposition" : "Add position"}
            </Button>
            {positioned && (
              <Button
                size="xs"
                startIcon={XClose}
                className="border-none shadow-sm"
                testId="delete-position-button"
                onClick={() => {
                  onChange(null);
                }}
              >
                Remove
              </Button>
            )}
            <MapPositionDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              onChange={(data) => {
                onChange({
                  latitude: data.latitude.toString(),
                  longitude: data.longitude.toString(),
                  elevation: null,
                });
              }}
              value={numericValue}
            />
          </MapPreviewOverlay>
        </div>
      )}
      <div className="flex justify-between gap-2 px-2 pt-2">
        <Button
          variant="linkgray"
          onClick={toggleFields}
          testId="toggle-map-fields-button"
          startIcon={<SwitchHorizontal1 className="h-4 w-4 text-grey-500" />}
        >
          Use {showFields ? "map" : "coordinates"}
        </Button>
      </div>
    </>
  );
};
